import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAppointmentCounter } from "../../api/Appointments/AppointmentDetail";
import { getStatusColor, getStatusText } from "../../../../utils";
import { ChevronRight, ClipboardList } from "lucide-react";

// Shared column sizing so header and rows stretch together to fill the card
const COLUMNS = {
  date: "flex-1 min-w-[130px]",
  timeSlot: "flex-1 min-w-[160px]",
  patientName: "flex-[1.4] min-w-[170px]",
  mode: "flex-1 min-w-[110px]",
  status: "flex-1 min-w-[150px]",
  action: "w-[60px] shrink-0",
};
const ROW_WIDTH = "w-full min-w-[780px]";

const TableRow = ({ booking, isHighlighted }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTimeSlot = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";

    const formatTime = (timeStr) => {
      const [hours, minutes] = timeStr.split(':');
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes}`;
    };

    const startFormatted = formatTime(startTime);
    const endFormatted = formatTime(endTime);

    // Determine if both times are AM or PM
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = parseInt(endTime.split(':')[0]);
    const startAmPm = startHour >= 12 ? 'PM' : 'AM';
    const endAmPm = endHour >= 12 ? 'PM' : 'AM';

    // If both times have same AM/PM, show it only at the end
    if (startAmPm === endAmPm) {
      return `${startFormatted}-${endFormatted} ${endAmPm}`;
    } else {
      return `${startFormatted} ${startAmPm}-${endFormatted} ${endAmPm}`;
    }
  };

  const formatMode = (mode) => {
    switch(mode) {
      case 'AUDIO': return 'Voice Call';
      case 'VIDEO': return 'Video Call';
      case 'CHAT': return 'Chat';
      case 'IN_PERSON': return 'In Person';
      default: return mode || 'N/A';
    }
  };

  const getPatientName = (booking) => {
    if (booking.anonymous) {
      return 'Anonymous';
    }
    return `${booking.title || ''} ${booking.firstName || ''} ${booking.lastName || ''}`.trim() || 'N/A';
  };


  return (
    <div className={`flex items-center h-11 ${ROW_WIDTH} border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0 text-[#334155] hover:bg-[#F8FAFC] transition`}>
      <div className={`flex gap-2.5 items-center px-2 py-0 ${COLUMNS.date}`}>
        <div className="text-[#334155] text-[12px] font-poppins font-normal whitespace-nowrap">
          {formatDate(booking.date)}
        </div>
      </div>
      <div className={`flex gap-2.5 items-center px-2 py-0 ${COLUMNS.timeSlot}`}>
        <div className="text-[#334155] text-[12px] font-poppins font-normal whitespace-nowrap">
          {formatTimeSlot(booking.startTime, booking.endTime)}
        </div>
      </div>
      <div className={`flex gap-2.5 items-center px-2 py-0 min-w-0 ${COLUMNS.patientName}`}>
        <div className="text-[#334155] text-[12px] font-poppins font-normal whitespace-nowrap truncate">
          {getPatientName(booking)}
        </div>
      </div>
      <div className={`flex gap-2.5 items-center px-2 py-0 ${COLUMNS.mode}`}>
        <div className="text-[#334155] text-[12px] font-poppins font-normal whitespace-nowrap">
          {formatMode(booking.mode)}
        </div>
      </div>
      <div className={`flex gap-2.5 items-center px-2 py-0 ${COLUMNS.status}`}>
        <div className="flex gap-1.5 items-center">
          <div className={`w-2 h-2 flex-shrink-0 rounded-full ${getStatusColor(booking.status, booking.currentStatus)}`}></div>
          <div className="text-[#4B61FF] text-[12px] leading-tight font-poppins font-semibold whitespace-nowrap">
            {getStatusText(booking.status, booking.currentStatus)}
          </div>
        </div>
      </div>
      <div className={`flex gap-2.5 items-center justify-center px-1 py-0 ${COLUMNS.action}`}>
        <div
          className="flex items-center justify-center w-[26px] h-[26px] rounded-full cursor-pointer bg-[#F1F5F9] hover:bg-[#E2E8F0] transition-colors"
          onClick={() => window.location.href = `/appointment-detail/${booking.appointmentId}`}
        >
          <ChevronRight size={14} className="text-[#64748B]" />
        </div>
      </div>
    </div>
  );
};

export default function RecentBookedTable() {
  const dispatch = useDispatch();
  const { appointmentCounter } = useSelector((state) => state.appointmentDetail);

  const handleViewAll = () => {
    window.location.href = "/appointments";
  };

  useEffect(() => {
    dispatch(getAppointmentCounter({
      offset: 0,
      limit: 5,
      orderBy: "id",
      direction: "DESC"
    }));
  }, [dispatch]);

  // Flatten appointments from grouped response and limit to 6 most recent
  const getRecentAppointments = () => {
    if (!appointmentCounter.data?.payload) {
      return [];
    }

    const allAppointments = [];

    // Flatten the date-grouped appointments
    Object.values(appointmentCounter.data.payload).forEach(dateGroup => {
      if (Array.isArray(dateGroup)) {
        allAppointments.push(...dateGroup);
      }
    });

    // Sort by date and time (most recent first) and limit to 5
    return allAppointments
      .sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.startTime}`);
        const dateB = new Date(`${b.date} ${b.startTime}`);
        return dateB - dateA;
      })
      .slice(0, 5);
  };

  const recentAppointments = getRecentAppointments();

  return (
    <div className="p-[24px] w-full h-full bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm">
      <div className="flex flex-col gap-4 w-full">
        {/* Header */}
        <div className="flex justify-between items-center gap-2 -mx-[24px] -mt-[24px] px-[24px] py-[14px] bg-[#E7EDF5] rounded-t-[15px]">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-white text-[#0D9488] flex-shrink-0">
              <ClipboardList size={18} />
            </div>
            <h2 className="text-base font-semibold text-[#171717] font-poppins mb-0">
              Recent Booked
            </h2>
          </div>
          <button
            type="button"
            className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap text-[#2563EB] font-poppins text-xs font-semibold bg-white hover:bg-[#F8FAFC] rounded-full px-3 py-1.5 border-0 transition-colors"
            onClick={handleViewAll}
          >
            View all
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Table */}
        <div className="flex flex-col w-full overflow-x-auto">
          {/* Header Row */}
          <div className={`flex items-center rounded-xl overflow-hidden bg-[#F8FAFC] ${ROW_WIDTH}`}>
            <div className={`flex gap-2.5 items-center px-2 py-2 ${COLUMNS.date}`}>
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase">
                Date
              </div>
            </div>
            <div className={`flex gap-2.5 items-center px-2 py-2 ${COLUMNS.timeSlot}`}>
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase">
                Time Slot
              </div>
            </div>
            <div className={`flex gap-2.5 items-center px-2 py-2 ${COLUMNS.patientName}`}>
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase">
                Patient Name
              </div>
            </div>
            <div className={`flex gap-2.5 items-center px-2 py-2 ${COLUMNS.mode}`}>
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase">
                Mode
              </div>
            </div>
            <div className={`flex gap-2.5 items-center px-2 py-2 ${COLUMNS.status}`}>
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase">
                Status
              </div>
            </div>
            <div className={COLUMNS.action}></div>
          </div>

          {/* Data Rows */}
          {appointmentCounter.loading ? (
            <div className="text-center text-[#64748B] text-sm font-poppins py-4">Loading...</div>
          ) : recentAppointments.length > 0 ? (
            recentAppointments.map((appointment, index) => (
              <TableRow
                key={appointment.appointmentId}
                booking={appointment}
                isHighlighted={index === 1 || index === 3}
              />
            ))
          ) : (
            <div className="text-center text-[#64748B] text-sm font-poppins py-4">No appointments found</div>
          )}
        </div>
      </div>
    </div>
  );
}